import { useEffect, useMemo, useState } from 'react'
import { KpiTile, StatusPill } from '../components/KpiTile.js'
import { NewQuoteChooser } from '../components/NewQuoteChooser.js'
import { fetchCommissions, fetchHsOptions, fetchQuotes, fetchRenewals, hubspotDealUrl, type DealPipeline, type QuoteRow, type QuoteStats, type RenewalsResponse, type ValueSplit } from '../lib/api.js'
import { renewalBuckets } from './Renewals.js'
import { GroupedBarChart, PipelineTable, TripleBarChart, gbpAxis, monthShort } from '../lib/charts.js'
import { formatGBPCompact, formatGBP, formatPct } from '@shared/money'
import { daysUntil, formatUKDate } from '@shared/dates'
import type { Partner } from '@shared/types'

/**
 * The cockpit (decision 112 overhaul) — KPI tiles that GO somewhere: every
 * bucket is a click into the relevant pre-filtered list, the quotes table is
 * gone (that's what the Quotes page is for), and staff sessions additionally
 * see the renewals book, per-salesperson buckets, and the activity charts.
 *
 * Charts follow the dataviz rules: one axis per chart (counts and £ are
 * separate panels, never dual-axis), thin rounded bars, recessive grid,
 * legend + tooltips, table fallback.
 */

type Scope = 'mine' | 'team'

export function Cockpit({ partner, role, onOpen, onGoQuotes, onGoRenewals, onGoCommissions, onGoAttention }: {
  partner: Partner
  role: 'admin' | 'user' | null
  onOpen: (quoteId: string) => void
  onGoQuotes: (filter?: { status?: string; owner?: string; stage?: string }) => void
  onGoRenewals: (filter?: { tile?: string; owner?: string; openId?: string }) => void
  /** Decision 183: the claimable tile opens the agent's statement, not a quote list. */
  onGoCommissions: () => void
  /** Decision 209: the Needs-attention tile opens its page. */
  onGoAttention: () => void
}) {
  const [scope, setScope] = useState<Scope>('team')
  const [rows, setRows] = useState<QuoteRow[]>([])
  // Decision 162: per-status aggregates over ALL rows (tile-safe under the list cap).
  const [statusAgg, setStatusAgg] = useState<Record<string, { n: number; valueSell: number }> | null>(null)
  // Decision 204: the new-business slice for the KPI tiles.
  const [statusAggNb, setStatusAggNb] = useState<Record<string, { n: number; valueSell: number }> | null>(null)
  // Decision 204: the two long tables open collapsed to their totals; the
  // choice is remembered per browser (the #199 idiom).
  const OPEN_KEY = 'w9.cockpit.tables'
  const [openTables, setOpenTables] = useState<{ open: boolean; recent: boolean }>(() => {
    try { const v = JSON.parse(localStorage.getItem(OPEN_KEY) ?? '{}'); return { open: v.open === true, recent: v.recent === true } } catch { return { open: false, recent: false } }
  })
  const toggleTable = (k: 'open' | 'recent') => setOpenTables((prev) => {
    const next = { ...prev, [k]: !prev[k] }
    try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)) } catch { /* private mode */ }
    return next
  })
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([])
  const [meId, setMeId] = useState<string | null>(null)
  const [stats, setStats] = useState<QuoteStats | null>(null)
  // Decision 201: the mapped pipelines order the pipeline tile's stages.
  const [dealPipelines, setDealPipelines] = useState<DealPipeline[]>([])
  // Decision 203: HubSpot portal id — the 30-day table links backfilled bookings to their deal.
  const [hsPortalId, setHsPortalId] = useState<string | null>(null)
  const [renewals, setRenewals] = useState<RenewalsResponse | null>(null)
  const [nudgeDays, setNudgeDays] = useState<{ unviewed: number; expiring: number; openHorizon?: number }>({ unviewed: 5, expiring: 7 })
  const [loading, setLoading] = useState(true)
  const [newQuoteOpen, setNewQuoteOpen] = useState(false)   // decision 210
  const [newNotice, setNewNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Decision 183: the real claimable figure, read from the ledger.
  const [claimable, setClaimable] = useState<number | null>(null)
  // Decision 201: "New business only" excludes renewal quotes from the
  // activity figures (renewal seq or renewal link — decision 105's definition).
  // Remembered per browser; the tiles and lists are never affected.
  const NB_KEY = 'w9.cockpit.newBusiness'
  const [newBusiness, setNewBusiness] = useState<boolean>(() => { try { return localStorage.getItem(NB_KEY) === '1' } catch { return false } })
  const toggleNewBusiness = (v: boolean) => { setNewBusiness(v); try { localStorage.setItem(NB_KEY, v ? '1' : '0') } catch { /* private mode */ } }

  const staff = role !== null

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchQuotes('open', undefined, { newBusiness })   // decision 162: the tiles derive from the FULL open set — the plain list is a capped newest-200 window (#161) · #201: stats honour the toggle
      .then((res) => {
        if (!live) return
        setRows(res.quotes); setUsers(res.users); setStatusAgg(res.statusAgg ?? null); setStatusAggNb(res.statusAggNewBusiness ?? null); setError(null)
        setMeId(res.meId ?? null)
        setStats(res.stats ?? null)
        setDealPipelines(res.dealPipelines ?? [])
        if (res.nudgeDays) setNudgeDays(res.nudgeDays)
      })
      .catch((e: Error) => live && setError(e.message))
      .finally(() => live && setLoading(false))
    // The renewals book is staff-only — the API refuses partner sessions, so
    // don't even ask on their behalf.
    if (staff) fetchRenewals().then((r) => { if (live) setRenewals(r) }).catch(() => {})
    if (staff) fetchHsOptions().then((o) => { if (live) setHsPortalId(o.portalId ?? null) }).catch(() => {})
    // Decision 183: agents only — non-fatal, the tile just stays quiet if the
    // ledger cannot be read.
    if (partner.mode === 'agent') {
      fetchCommissions()
        .then((r) => { if (live) setClaimable(r.totals.claimable) })
        .catch(() => {})
    } else setClaimable(null)
    return () => { live = false }
  }, [partner.id, partner.mode, staff, newBusiness])

  // "Mine" scopes the quote tiles + lists to the signed-in person (meId).
  const mine = scope === 'mine' && meId != null
  const quotes = useMemo(
    () => (mine ? rows.filter((q) => q.ownerId === meId) : rows),
    [rows, mine, meId],
  )
  /** Tile clicks carry the scope: "mine" lands the list pre-filtered to me. */
  const scopedOwner = mine ? meId ?? undefined : undefined

  // Decision 162: the fetch is server-filtered to OPEN rows (the full open set —
  // the plain list is a capped newest-200 window, #161), so open-derived lists
  // and buckets are complete; decided/accepted figures come from statusAgg,
  // which aggregates over ALL rows server-side.
  // Decision 251: open includes expired quotes inside the open horizon (the server flags them price-lapsed).
  const open = quotes.filter((q) => ['sent', 'viewed'].includes(q.status) || q.priceLapsed === true)
  const acceptedN = statusAgg?.accepted?.n ?? 0
  const declinedN = statusAgg?.declined?.n ?? 0
  const decidedN = acceptedN + declinedN
  const expiringSoon = open.filter((q) => {
    if (!q.validUntil) return false
    const d = daysUntil(q.validUntil)
    return d >= 0 && d <= nudgeDays.expiring
  })

  // Decision 100: "needs attention" — unchanged, still the first thing to read.
  const nudges = quotes.flatMap((q) => {
    if (meId && q.ownerId !== meId) return []
    const reasons: Array<{ kind: 'unviewed' | 'expiring' | 'lapsed'; label: string }> = []
    if (q.status === 'sent' && q.sentAt) {
      const days = Math.floor((Date.now() - new Date(q.sentAt).getTime()) / 86_400_000)
      if (days >= nudgeDays.unviewed) reasons.push({ kind: 'unviewed', label: `not viewed — sent ${days}d ago` })
    }
    if (['sent', 'viewed'].includes(q.status) && q.validUntil) {
      const d = daysUntil(q.validUntil)
      if (d >= 0 && d <= nudgeDays.expiring) reasons.push({ kind: 'expiring', label: d === 0 ? 'expires today' : `expires in ${d}d` })
    }
    // Decision 251: the third nudge — price lapsed, still open, nobody has decided.
    if (q.priceLapsed && q.validUntil) {
      const d = -daysUntil(q.validUntil)
      reasons.push({ kind: 'lapsed', label: d <= 0 ? 'price lapsed today — undecided' : `price lapsed ${d}d ago — undecided` })
    }
    return reasons.length > 0 ? [{ quote: q, reasons }] : []
  })
  const acceptRate = decidedN ? acceptedN / decidedN : 0
  const openValue = open.reduce((s, q) => s + q.valueSell, 0)
  // Decision 204: the new-business slice of each tile (renewal = seq or link).
  const openNb = open.filter((q) => !q.isRenewal)
  const openValueNb = openNb.reduce((s, q) => s + q.valueSell, 0)
  const acceptedNbN = statusAggNb?.accepted?.n ?? 0, declinedNbN = statusAggNb?.declined?.n ?? 0, decidedNbN = acceptedNbN + declinedNbN
  const expiringSoonNb = expiringSoon.filter((q) => !q.isRenewal)

  const marginValue = partner.mode === 'reseller' ? open.reduce((s, q) => s + (q.valueSell - q.valueBuy), 0) : 0

  // ---- staff extras: renewals buckets, per-person, at-risk spotlight -------
  const renewalRows = renewals?.renewals ?? []
  const windowDays = renewals?.windowDays ?? 120
  const buckets = useMemo(() => renewalBuckets(renewalRows, windowDays), [renewalRows, windowDays])

  // Per-salesperson buckets: open quotes (this partner) + renewals due inside
  // the window, matched through the People roster by name (both sides resolve
  // partner_users.name).
  const people = useMemo(() => {
    const byId = new Map(users.map((u) => [u.id, u.name]))
    const acc = new Map<string, { id: string | null; name: string; openN: number; openValue: number; renewalsN: number }>()
    for (const q of rows) {
      if (!['sent', 'viewed'].includes(q.status)) continue
      const name = q.ownerId ? (byId.get(q.ownerId) ?? q.ownerName ?? '—') : 'No owner'
      const key = q.ownerId ?? 'none'
      const cur = acc.get(key) ?? { id: q.ownerId, name, openN: 0, openValue: 0, renewalsN: 0 }
      cur.openN += 1; cur.openValue += q.valueSell
      acc.set(key, cur)
    }
    for (const r of buckets.dueNow) {
      const name = r.ownerName ?? 'No owner'
      const entry = [...acc.values()].find((p) => p.name === name)
      if (entry) entry.renewalsN += 1
      else {
        const key = `r:${name}`
        const cur = acc.get(key) ?? { id: users.find((u) => u.name === name)?.id ?? null, name, openN: 0, openValue: 0, renewalsN: 0 }
        cur.renewalsN += 1
        acc.set(key, cur)
      }
    }
    return [...acc.values()].sort((a, b) => b.openValue - a.openValue || b.renewalsN - a.renewalsN)
  }, [rows, users, buckets.dueNow])

  // ---- chart series (decision 201 reshape) ---------------------------------
  const months = stats?.months ?? []
  const fyMonth = stats?.fy?.start.slice(0, 7)
  const activity = months.map((m) => ({ label: monthShort(m.month), month: m.month, a: m.sent, b: m.accepted }))
  const valueActivity = months.map((m) => ({ label: monthShort(m.month), month: m.month, a: m.sentValue, b: m.acceptedValue }))
  const splitSeries = months.map((m) => ({ label: monthShort(m.month), month: m.month, a: m.acceptedOneOff, b: m.acceptedArr, c: m.acceptedValue }))
  // Acceptance detail: the 12 rolling months (under the chart's 12) + the
  // financial year to date + the rolling 12-month total (the sum of the twelve
  // columns shown — Mark's amendment, replacing "last FY same span").
  const monthSplits = months.map((m) => ({ n: m.accepted, oneOff: m.acceptedOneOff, arr: m.acceptedArr, tcv: m.acceptedValue }))
  const rolling12: ValueSplit = monthSplits.reduce((t, v) => ({ n: t.n + v.n, oneOff: t.oneOff + v.oneOff, arr: t.arr + v.arr, tcv: t.tcv + v.tcv }), { n: 0, oneOff: 0, arr: 0, tcv: 0 })
  const detailCols: Array<{ label: string; v: ValueSplit; strong?: boolean; fy?: boolean }> = [
    ...months.map((m, i) => ({ label: monthShort(m.month), v: monthSplits[i], fy: m.month === fyMonth })),
    ...(stats?.fy ? [{ label: 'Fin YTD', v: stats.fy.ytd, strong: true }] : []),
    { label: 'Rolling 12m', v: rolling12, strong: true },
  ]
  const gbp0 = (v: number) => formatGBPCompact(Math.round(v))
  // Pipeline: stages in mapped-pipeline order, then anything unmapped (e.g. a
  // contracts stage a deal moved on to), "No deal stage" last. Partners: status.
  const pipelineRows = useMemo(() => {
    const b = stats?.pipeline?.buckets ?? []
    // Decision 215: margin rides each row. (#245: no stage weight / weighted TCV.)
    const toRow = (x: ValueSplit & { key: string; label: string; buy?: number; marginPct?: number | null }) =>
      ({ key: x.key, label: x.label, n: x.n, a: x.oneOff, b: x.arr, c: x.tcv, buy: x.buy ?? 0, marginPct: x.marginPct ?? null })
    if (stats?.pipeline?.by !== 'stage') {
      const order = ['sent', 'viewed']
      return [...b].sort((x, y) => order.indexOf(x.key) - order.indexOf(y.key)).map(toRow)
    }
    const seen = new Set<string>()
    const ordered: typeof b = []
    for (const p of dealPipelines) for (const st of p.stages) {
      const hit = b.find((x) => x.key === st.id)
      if (hit && !seen.has(hit.key)) { ordered.push(hit); seen.add(hit.key) }
    }
    const rest = b.filter((x) => !seen.has(x.key) && x.key !== '').sort((x, y) => x.label.localeCompare(y.label))
    const none = b.filter((x) => x.key === '')
    const rows = [...ordered, ...rest, ...none].map(toRow)
    // Two pipelines can both have a "Quotation Sent" — seen live on day one.
    // When labels collide, say which pipeline each stage belongs to.
    const counts = new Map<string, number>()
    for (const r of rows) counts.set(r.label, (counts.get(r.label) ?? 0) + 1)
    const pipelineOf = (stageId: string) => dealPipelines.find((p) => p.stages.some((st) => st.id === stageId))?.label
    return rows.map((r) => (counts.get(r.label) ?? 0) > 1 && r.key !== ''
      ? { ...r, label: `${r.label} · ${pipelineOf(r.key) ?? 'other pipeline'}` }
      : r)
  }, [stats?.pipeline, dealPipelines])
  // Decision 202: the rate uses PORTAL acceptances only — backfilled months
  // carry no declines, so counting them would read as 100%.
  const rateSeries = months.map((m) => ({
    label: monthShort(m.month), month: m.month,
    v: (m.acceptedPortal + m.declined) > 0 ? m.acceptedPortal / (m.acceptedPortal + m.declined) : null,
  }))
  const anyActivity = months.some((m) => m.sent > 0 || m.accepted > 0)

  // Decision 201 (Mark): the top 20 open quotes, full width, with the split, stage, sent date and owner.
  // The toggle reaches this table too: under "new business only" renewal quotes drop out.
  const openForTable = newBusiness ? open.filter((q) => !q.isRenewal) : open
  const topOpen = [...openForTable].sort((a, b) => b.valueSell - a.valueSell).slice(0, 20)
  // Decision 203: totals for the rows shown and for EVERY open quote, so each
  // row's share ("% of open") is of the whole open book, not of the top 20.
  const sumSplit = (rows: QuoteRow[]): ValueSplit => rows.reduce((t, q) => ({ n: t.n + 1, oneOff: t.oneOff + q.oneOffSell, arr: t.arr + q.annualSell, tcv: t.tcv + q.valueSell }), { n: 0, oneOff: 0, arr: 0, tcv: 0 })
  const topTotal = sumSplit(topOpen), openTotal = sumSplit(openForTable)
  const pctOfOpen = (v: number) => openTotal.tcv > 0 ? `${(v / openTotal.tcv * 100).toFixed(1)}%` : '—'
  // Decision 203: accepted in the last rolling 30 days — portal + backfill, newest first.
  const recent = stats?.recentAccepted ?? []
  const recentTotal = recent.reduce((t, r) => ({ n: t.n + 1, oneOff: t.oneOff + r.oneOff, arr: t.arr + r.arr, tcv: t.tcv + r.tcv }), { n: 0, oneOff: 0, arr: 0, tcv: 0 })

  if (loading) return <p className="muted">Loading cockpit…</p>
  if (error) return <p className="pill pill--stop">{error}</p>

  const clickTile = (title: string, onClick: () => void, tile: React.ReactNode) => (
    <div role="button" tabIndex={0} style={{ cursor: 'pointer' }} title={title}
      onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick() }}>
      {tile}
    </div>
  )

  return (
    <>
      {newQuoteOpen && (
        <NewQuoteChooser onClose={() => setNewQuoteOpen(false)} onError={(m) => { setNewQuoteOpen(false); setNewNotice(m) }}
          supplierImport={staff && partner.mode === 'internal'}
          onCreated={(id, n) => { setNewQuoteOpen(false); if (n) setNewNotice(n); onOpen(id) }} />
      )}
      {newNotice && <div className="banner" style={{ marginBottom: 12 }}><span>{newNotice}</span><button className="btn btn--ghost btn--sm" onClick={() => setNewNotice(null)}>Dismiss</button></div>}
      <div className="page-head">
        <div>
          <h1>Cockpit</h1>
          <p>
            {partner.name} · {partner.mode} mode
            {partner.isTest && <span className="pill pill--warn" style={{ marginLeft: 8 }}>test partner</span>}
          </p>
        </div>
        <div className="toolbar">
          <div className="toggle" role="group" aria-label="Quote scope">
            <button aria-pressed={scope === 'mine'} onClick={() => setScope('mine')} disabled={meId == null}
              title={meId == null ? 'Key sessions have no “me” — sign in via SSO or PIN' : 'Only quotes you own'}>Mine</button>
            <button aria-pressed={scope === 'team'} onClick={() => setScope('team')}>Team</button>
          </div>
          {/* Decision 210: blank or from a template — one chooser, shared with the Quotes list. */}
          <button className="btn btn--primary" onClick={() => setNewQuoteOpen(true)} data-testid="new-quote">+ New quote</button>
        </div>
      </div>

      {quotes.length === 0 && (
        <div className="banner">
          <strong>No quotes yet{mine ? ' of yours' : ''}.</strong>
          <span>Start one with “New quote” — this cockpit fills up as quotes are raised.</span>
        </div>
      )}

      {/* Every tile is a doorway to the relevant filtered list (112). */}
      <div className="tiles">
        {/* Decision 209: the Needs-attention panel moved to its own page — this tile counts it and opens it. */}
        {clickTile('Open the Needs attention page', () => onGoAttention(),
          <KpiTile label="Needs attention" value={String(nudges.length)} sub={nudges.length ? 'not viewed · expiring · price lapsed' : 'nothing to chase'} />)}
        {clickTile('Open the quotes list — open quotes', () => onGoQuotes({ status: 'open', owner: scopedOwner }),
          <KpiTile accent label="Open quotes" value={String(open.length)} sub={formatGBPCompact(openValue)}
            split={{ value: String(openNb.length), sub: formatGBPCompact(openValueNb) }} />)}
        {clickTile('Open the quotes list — viewed, not decided', () => onGoQuotes({ status: 'viewed', owner: scopedOwner }),
          <KpiTile label="Awaiting decision" value={String(open.filter((q) => q.status === 'viewed').length)} sub="viewed, not decided"
            split={{ value: String(openNb.filter((q) => q.status === 'viewed').length) }} />)}
        {clickTile('Open the quotes list — accepted', () => onGoQuotes({ status: 'accepted', owner: scopedOwner }),
          <KpiTile label="Acceptance rate" value={decidedN ? formatPct(acceptRate, 0) : '—'} sub={`${acceptedN} of ${decidedN} decided`}
            split={{ value: decidedNbN ? formatPct(acceptedNbN / decidedNbN, 0) : '—', sub: `${acceptedNbN} of ${decidedNbN}` }} />)}
        {clickTile('Open the quotes list — expiring soon', () => onGoQuotes({ status: 'expiring', owner: scopedOwner }),
          <KpiTile label={`Expiring in ${nudgeDays.expiring} days`} value={String(expiringSoon.length)} sub={expiringSoon.length ? 'chase or supersede' : 'nothing urgent'}
            split={{ value: String(expiringSoonNb.length) }} />)}
        <KpiTile label="Avg time to decision" value={stats?.avgDecisionDays != null ? `${stats.avgDecisionDays}d` : '—'}
          sub={stats?.decidedLast90 ? `${stats.decidedLast90} decided in 90 days` : 'no decisions in 90 days'}
          split={{ value: stats?.avgDecisionDaysNewBusiness != null ? `${stats.avgDecisionDaysNewBusiness}d` : '—', sub: `${stats?.decidedLast90NewBusiness ?? 0} decided` }} />
        {/* Decision 183: the LEDGER's claimable total — pending rows only, at
            the amounts frozen on acceptance. It used to be accepted-count ×
            the partner's default fee, which ignored per-quote overrides and
            counted fees already paid. */}
        {partner.mode === 'agent' && clickTile('Open the claimable statement', () => onGoCommissions(),
          <KpiTile label="Claimable fees" value={claimable == null ? '—' : formatGBPCompact(claimable)}
            sub={claimable == null ? 'loading…' : 'claim, then invoice Wave9'} />)}
        {partner.mode === 'reseller' && clickTile('Open the quotes list — open quotes', () => onGoQuotes({ status: 'open', owner: scopedOwner }),
          <KpiTile label="Margin on open" value={formatGBPCompact(marginValue)} sub="your sell less your buy" />)}
      </div>

      {/* Staff only: the renewals book at a glance — same buckets as the
          Renewals page tiles, each a click into that page pre-filtered. */}
      {staff && renewals && (
        <>
          <h2 className="small muted" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', margin: '2px 0 8px' }}>Renewals</h2>
          <div className="tiles">
            {clickTile('Open Renewals — inside the window', () => onGoRenewals({ tile: 'dueNow' }),
              <KpiTile accent label="Open renewals" value={String(buckets.dueNow.length)} sub={`inside ${windowDays} days`} />)}
            {clickTile('Open Renewals — due inside 40 days', () => onGoRenewals({ tile: 'due40' }),
              <KpiTile label="Due inside 40 days" value={String(buckets.due40.length)} sub="chase now" />)}
            {clickTile('Open Renewals — nothing quoted yet', () => onGoRenewals({ tile: 'noQuote' }),
              <KpiTile label="Nothing quoted yet" value={String(buckets.noQuote.length)} sub="no quote or linked deal" />)}
            {clickTile(`Open Renewals — ${windowDays} days to 1 year`, () => onGoRenewals({ tile: 'far' }),
              <KpiTile label={`${windowDays} days – 1 year`} value={String(buckets.far.length)} sub="renewal stage, date further out" />)}
            {clickTile('Open Renewals — concluded', () => onGoRenewals({ tile: 'concluded' }),
              <KpiTile label="Concluded" value={String(buckets.closed.length)} sub="rolled over · renewed · lost" />)}
          </div>
        </>
      )}

      {/* Staff only: who is carrying what — open quotes + renewals per person. */}
      {staff && people.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__head">
            <h2>By salesperson</h2>
            <span className="muted small">open quotes · renewals due inside {windowDays} days — click a figure to see the list</span>
          </div>
          <div className="card__body">
            <div className="tiles" style={{ marginBottom: 0 }}>
              {people.map((p) => (
                <div key={`${p.id ?? p.name}`} className="tile">
                  <div className="tile__label">{p.name}</div>
                  <div className="rowflex" style={{ gap: 14, marginTop: 7, alignItems: 'baseline' }}>
                    <button className="btn btn--ghost" style={{ padding: 0, fontSize: 19, fontWeight: 650 }}
                      title="Their open quotes"
                      onClick={() => onGoQuotes({ status: 'open', owner: p.id ?? 'none' })}>
                      {p.openN}<span className="small muted" style={{ fontWeight: 400 }}> open</span>
                    </button>
                    <button className="btn btn--ghost" style={{ padding: 0, fontSize: 19, fontWeight: 650 }}
                      title="Their renewals inside the window"
                      onClick={() => onGoRenewals(p.name === 'No owner' ? { tile: 'dueNow', owner: 'none' } : { tile: 'dueNow', owner: p.name })}>
                      {p.renewalsN}<span className="small muted" style={{ fontWeight: 400 }}> renewals</span>
                    </button>
                  </div>
                  <div className="tile__sub">{formatGBPCompact(p.openValue)} open value</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Activity (decision 201, re-laid out the same day at Mark's review):
          separate sections in story order — sent vs accepted · what was
          accepted · what's in the pipeline · rate & biggest open. The one
          two-column row is the sent/accepted pair (same chart, same months).
          Money splits are GROUPED, never stacked (TCV ≠ one-off + ARR). */}
      {/* Decision 201: ONE toggle for the whole activity section (Mark: a
          per-card toggle "saves confusion" only if there is exactly one). It
          drives every chart, the FY table, the pipeline AND the open-quotes
          table; each card's subtitle repeats the state. */}
      <div className={`activity${newBusiness ? ' activity--nb' : ''}`} data-testid="activity" data-nb={newBusiness ? '1' : '0'}>
      <div className="rowflex" style={{ justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 10px', gap: 12, flexWrap: 'wrap' }} data-testid="activity-bar">
        <div>
          <h2 style={{ margin: 0 }}>Activity</h2>
          <span className="muted small">{mine ? 'whole team (this section is not scoped by Mine/Team)' : `all ${partner.name} quotes`} · everything below follows this toggle</span>
        </div>
        <div className="toggle" role="group" aria-label="Include renewals" data-testid="nb-toggle">
          <button aria-pressed={!newBusiness} onClick={() => toggleNewBusiness(false)} title="Every quote, renewals included">All quotes</button>
          <button aria-pressed={newBusiness} onClick={() => toggleNewBusiness(true)} title="Exclude renewal quotes (W9R quotes and deals linked to a renewal) — new-business totals">New business only</button>
        </div>
      </div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__head">
          <h2>Sent vs accepted — last 12 months</h2>
          <span className="muted small">{newBusiness ? 'new business only' : 'all quotes'} · sent is net of revisions</span>
        </div>
        <div className="card__body">
          {!anyActivity && <p className="muted small">No sends or acceptances in the last 12 months yet — the charts fill in as quotes go out.</p>}
          {anyActivity && (
            <div className="split" style={{ alignItems: 'start' }}>
              <div>
                <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>Quotes</div>
                <GroupedBarChart data={activity} names={['Sent', 'Accepted']} fyMonth={fyMonth} />
                <p className="tiny muted" style={{ margin: '4px 0 0' }}>A quote later superseded by a revision is not counted — the revision is, in its own month. Dashed line: 1 April. <strong>Sent data starts with the portal (August 2026)</strong> — earlier months show accepted bookings only.</p>
              </div>
              <div>
                <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>Value (TCV £)</div>
                <GroupedBarChart data={valueActivity} names={['Sent', 'Accepted']} format={(v) => formatGBPCompact(v)} axisFormat={gbpAxis} fyMonth={fyMonth} />
                <p className="tiny muted" style={{ margin: '4px 0 0' }}>Sent at the quote's own value; accepted from the frozen record — lower when the customer left optional lines out.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {anyActivity && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__head">
            <h2>What was accepted — one-off · ARR · TCV</h2>
            <span className="muted small">{newBusiness ? 'new business only' : 'all quotes'} · includes HubSpot bookings before the portal · ARR = annualised recurring value · TCV = one-off + recurring over the contract term</span>
          </div>
          <div className="card__body">
            <TripleBarChart wide data={splitSeries} names={['One-off', 'ARR', 'TCV']} format={(v) => formatGBPCompact(v)} axisFormat={gbpAxis} fyMonth={fyMonth}
              rate={rateSeries.map((r) => r.v)} />
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="small" data-testid="acceptance-detail">
                <thead>
                  <tr>
                    <th></th>
                    {detailCols.map((c) => (
                      <th key={c.label} className="num"
                        title={c.label === 'Fin YTD' ? `Financial year to date — from ${stats?.fy ? formatUKDate(stats.fy.start) : '1 April'}` : c.label === 'Rolling 12m' ? 'The twelve months shown, added up' : undefined}
                        style={{ padding: '7px 8px', ...(c.strong ? { color: 'var(--chart-emphasis)' } : {}), ...(c.fy ? { borderLeft: '1px dashed var(--ink-soft)' } : {}) }}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {([
                    ['Quotes accepted', (v: ValueSplit) => String(v.n)],
                    ['One-off', (v: ValueSplit) => gbp0(v.oneOff)],
                    ['ARR', (v: ValueSplit) => gbp0(v.arr)],
                    ['TCV', (v: ValueSplit) => gbp0(v.tcv)],
                  ] as Array<[string, (v: ValueSplit) => string]>).map(([label, f]) => (
                    <tr key={label}>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap', padding: '7px 8px' }}>{label}</td>
                      {detailCols.map((c) => (
                        <td key={c.label} className="num"
                          style={{ padding: '7px 8px', ...(c.strong ? { fontWeight: 600 } : {}), ...(c.fy ? { borderLeft: '1px dashed var(--ink-soft)' } : {}) }}>{f(c.v)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="tiny muted" style={{ margin: '6px 0 0' }}>Financial year starts 1 April (the dashed rule). Fin YTD = this financial year to date; Rolling 12m = the twelve months shown, added up. Whole pounds.</p>
          </div>
        </div>
      )}

      {anyActivity && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__head">
            <h2>Pipeline — open quotes by {stats?.pipeline?.by === 'stage' ? 'HubSpot deal stage' : 'status'}</h2>
            <span className="muted small">{newBusiness ? 'new business only' : 'all quotes'} · sent and viewed quotes · click a stage to open those quotes</span>
          </div>
          {pipelineRows.length === 0
            ? <div className="card__body"><p className="muted small">Nothing open right now.</p></div>
            : <PipelineTable rows={pipelineRows}
                onRow={(key) => stats?.pipeline?.by === 'stage'
                  ? onGoQuotes({ status: 'open', stage: key === '' ? 'none' : key })
                  : onGoQuotes({ status: key })} />}
        </div>
      )}

      {/* Decision 201 (Mark): the biggest open quotes, full width, top 20 —
          stage (staff only), the split, date sent, owner. Reads the FULL open
          set (#162), never the capped list. */}
      <div className="card" style={{ marginBottom: 16 }} data-testid="biggest-open-card" data-open={openTables.open ? '1' : '0'}>
        <div className="card__head" role="button" tabIndex={0} style={{ cursor: 'pointer' }} aria-expanded={openTables.open}
          title={openTables.open ? 'Collapse to the totals' : 'Show the 20 biggest open quotes'}
          onClick={() => toggleTable('open')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTable('open') } }}>
          <h2><span aria-hidden="true" style={{ display: 'inline-block', width: 16, color: 'var(--ink-soft)' }}>{openTables.open ? '▾' : '▸'}</span>Biggest open quotes</h2>
          <span className="muted small">{newBusiness ? 'new business only' : 'all quotes'} · top {topOpen.length} of {openForTable.length} open by TCV{mine ? ' · yours' : ''} · {openTables.open ? 'click a row to open the quote' : 'click to expand'}</span>
        </div>
        {topOpen.length === 0
          ? <div className="card__body"><p className="muted small">Nothing open right now.</p></div>
          : <div className="table-wrap">
              <table className="small" data-testid="biggest-open">
                <thead>
                  <tr>
                    <th>Quote</th>
                    <th>Customer</th>
                    <th>Status</th>
                    {staff && <th>Deal stage</th>}
                    <th>Owner</th>
                    <th>Sent</th>
                    <th className="num">One-off</th>
                    <th className="num">ARR</th>
                    <th className="num">TCV</th>
                    <th className="num" title="This quote's TCV as a share of every open quote's TCV">% of open</th>
                  </tr>
                </thead>
                <tbody hidden={!openTables.open}>
                  {topOpen.map((q) => (
                    <tr key={q.id} onClick={() => onOpen(q.id)} title="Open the quote" style={{ cursor: 'pointer' }}>
                      <td><strong>{q.quoteNumber}</strong></td>
                      <td>{q.customer}</td>
                      <td><StatusPill status={q.status} split={q.splitAt != null} splitMode={q.splitMode} lapsed={q.priceLapsed} /></td>
                      {staff && <td>{q.dealStageLabel ?? <span className="muted">—</span>}</td>}
                      <td>{q.ownerName ?? <span className="muted">—</span>}</td>
                      <td>{q.sentAt ? formatUKDate(q.sentAt) : <span className="muted">—</span>}</td>
                      <td className="num">{formatGBP(q.oneOffSell)}</td>
                      <td className="num">{formatGBP(q.annualSell)}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{formatGBP(q.valueSell)}</td>
                      <td className="num muted">{pctOfOpen(q.valueSell)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot data-testid="biggest-open-totals">
                  <tr style={{ fontWeight: 600, borderTop: '2px solid var(--line)' }}>
                    <td colSpan={staff ? 6 : 5}>Top {topTotal.n} shown</td>
                    <td className="num">{formatGBP(topTotal.oneOff)}</td>
                    <td className="num">{formatGBP(topTotal.arr)}</td>
                    <td className="num">{formatGBP(topTotal.tcv)}</td>
                    <td className="num">{pctOfOpen(topTotal.tcv)}</td>
                  </tr>
                  <tr style={{ fontWeight: 600 }}>
                    <td colSpan={staff ? 6 : 5}>All {openTotal.n} open quotes</td>
                    <td className="num">{formatGBP(openTotal.oneOff)}</td>
                    <td className="num">{formatGBP(openTotal.arr)}</td>
                    <td className="num">{formatGBP(openTotal.tcv)}</td>
                    <td className="num">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>}
      </div>

      {/* Decision 203 (Mark): everything accepted in the last rolling 30 days,
          totalised — portal quotes (click → the quote) and backfilled HubSpot
          bookings (click → the deal), newest first; follows the toggle. */}
      <div className="card" style={{ marginBottom: 16 }} data-testid="recent-accepted-card" data-open={openTables.recent ? '1' : '0'}>
        <div className="card__head" role="button" tabIndex={0} style={{ cursor: 'pointer' }} aria-expanded={openTables.recent}
          title={openTables.recent ? 'Collapse to the totals' : 'Show every acceptance in the last 30 days'}
          onClick={() => toggleTable('recent')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTable('recent') } }}>
          <h2><span aria-hidden="true" style={{ display: 'inline-block', width: 16, color: 'var(--ink-soft)' }}>{openTables.recent ? '▾' : '▸'}</span>Accepted — last 30 days</h2>
          <span className="muted small">{newBusiness ? 'new business only' : 'all quotes'} · rolling 30 days to today · portal quotes and HubSpot bookings · {openTables.recent ? 'click a row to open it' : 'click to expand'}</span>
        </div>
        {recent.length === 0
          ? <div className="card__body"><p className="muted small">Nothing accepted in the last 30 days.</p></div>
          : <div className="table-wrap">
              <table className="small" data-testid="recent-accepted">
                <thead>
                  <tr>
                    <th>Accepted</th>
                    <th>Quote / deal</th>
                    <th>Customer</th>
                    <th>Type</th>
                    <th>Owner</th>
                    <th className="num">One-off</th>
                    <th className="num">ARR</th>
                    <th className="num">TCV</th>
                  </tr>
                </thead>
                <tbody hidden={!openTables.recent}>
                  {recent.map((r) => {
                    const dealUrl = r.source === 'hubspot' ? hubspotDealUrl(hsPortalId, r.hsDealId) : null
                    const go = r.source === 'portal' && r.id ? () => onOpen(r.id!) : dealUrl ? () => window.open(dealUrl, '_blank', 'noopener') : undefined
                    return (
                      <tr key={`${r.source}:${r.id ?? r.hsDealId}`} onClick={go} title={r.source === 'portal' ? 'Open the quote' : dealUrl ? 'Open the HubSpot deal' : undefined} style={{ cursor: go ? 'pointer' : 'default' }}>
                        <td>{formatUKDate(r.acceptedAt)}</td>
                        <td>
                          <strong>{r.quoteNumber ?? `HubSpot ${r.hsDealId ?? ''}`}</strong>
                          {r.source === 'hubspot' && <span className="pill tiny" style={{ marginLeft: 6 }} title="From the HubSpot bookings backfill, not a portal quote">booking</span>}
                        </td>
                        <td>{r.customer ?? r.title ?? <span className="muted">—</span>}</td>
                        <td>{r.kind === 'renewal' ? 'Renewal' : 'New business'}</td>
                        <td>{r.ownerName ?? <span className="muted">—</span>}</td>
                        <td className="num">{formatGBP(r.oneOff)}</td>
                        <td className="num">{formatGBP(r.arr)}</td>
                        <td className="num" style={{ fontWeight: 600 }}>{formatGBP(r.tcv)}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot data-testid="recent-accepted-totals">
                  <tr style={{ fontWeight: 600, borderTop: '2px solid var(--line)' }}>
                    <td colSpan={5}>{recentTotal.n} accepted in 30 days</td>
                    <td className="num">{formatGBP(recentTotal.oneOff)}</td>
                    <td className="num">{formatGBP(recentTotal.arr)}</td>
                    <td className="num">{formatGBP(recentTotal.tcv)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>}
      </div>
      </div>
    </>
  )
}
